import { type Accessor, createEffect, createSignal, on } from "solid-js";
import { popOut } from "../utils/widget-motion";

export type Presence<T> = {
  /** Mirrors the source, but lingers through the exit animation before clearing. */
  current: Accessor<T | null>;
  /** Ref setter for the element to animate out on close. */
  setEl: (el: HTMLElement) => void;
};

type PresenceOptions = {
  exit?: (el: HTMLElement) => Promise<void>;
};

/**
 * Keep a store-driven element mounted long enough to play its exit animation.
 * When the source turns null the element pops out, then the mirror clears; a
 * new value arriving mid-exit wins immediately.
 */
export function createPresence<T>(
  source: Accessor<T | null | undefined>,
  options: PresenceOptions = {},
): Presence<T> {
  const [current, setCurrent] = createSignal<T | null>(source() ?? null);
  let el: HTMLElement | null = null;
  let epoch = 0;

  createEffect(
    on(
      source,
      (value) => {
        epoch += 1;
        const mine = epoch;
        if (value) {
          // Re-arrival can interrupt a running exit; the element survives (the
          // signal may not even change identity), so undo the exit's styling.
          const node = el;
          if (node?.isConnected && typeof node.getAnimations === "function") {
            for (const animation of node.getAnimations()) {
              try {
                animation.cancel();
              } catch {
                /* ignore */
              }
            }
            node.style.removeProperty("opacity");
            node.style.removeProperty("transform");
            delete node.dataset.plExiting;
          }
          setCurrent(() => value);
          return;
        }
        const node = el;
        if (!node || !node.isConnected) {
          setCurrent(null);
          return;
        }
        void (options.exit ?? popOut)(node).then(() => {
          if (epoch === mine) setCurrent(null);
        });
      },
      { defer: true },
    ),
  );

  return {
    current,
    setEl: (node) => {
      el = node;
    },
  };
}
